// The instructor's side of a pending booking (PA-2): the student's payment screenshot, with
// Confirm and Decline beside it, so the instructor checks the payment without leaving the app.
// No screenshot shows until the student marks payment sent. The screenshot opens through the
// signed-in private file route (ADR 0005); tapping it enlarges it. Decline opens a confirm step
// with an optional reason for the student's email (IW-4); Keep booking closes it and sends nothing.
//
// A booking a booking payment holds (coveredByPayment, JTP-6) has no Confirm: Paid through Ropida
// confirms it when JTPay reports the payment Paid. Decline stays, so the instructor can still say no.
//
// onChanged({ status, message }) runs after Confirm or Decline lands, and after a 409 (the
// booking changed elsewhere, e.g. the student cancelled). The caller refreshes its list then.
import React from 'react';
import { api } from '../lib/api';
import { privateFileUrl } from '../lib/privateFiles';
import { Btn } from './index';
import { ImageLightbox } from './ImageLightbox';
import { PaymentSentBadge, isPaymentSent } from './PaymentSentBadge';
import { DeclineConfirm, declineErrorMessage, declineRequestArgs } from './DeclineBookingButton';
import { palette as COLORS, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";
const CONFLICT_STATUS = 409;

export const CONFIRMS_ON_PAYMENT_NOTE = 'Paid through Ropida confirms this booking when the payment arrives.';

// Kept word for word from the dashboard's earlier snackbar: the payments and booking e2e specs
// and demos 07 and 12 wait for this text.
export const CONFIRMED_MESSAGE = 'Booking accepted.';
export const DECLINED_MESSAGE = 'Booking declined.';
export const ALREADY_UPDATED_MESSAGE = 'This booking was already updated. Your list now shows its latest status.';

const ACTIONS = {
  confirm: { path: 'accept', status: 'confirmed', message: CONFIRMED_MESSAGE, failed: 'We could not confirm this booking. Check your connection and try again.' },
  decline: { path: 'decline', status: 'declined', message: DECLINED_MESSAGE, failed: 'We could not decline this booking. Check your connection and try again.' },
};

export function PaymentProofPanel({ booking, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Decline opens a confirm step with an optional reason (IW-4.1); nothing is sent until it is confirmed.
  const [declining, setDeclining] = React.useState(false);
  // A ref, not state: two taps in one frame both see busy=false in state.
  const sending = React.useRef(false);

  if (!booking) return null;
  const proofUrl = isPaymentSent(booking) ? privateFileUrl(booking.paymentProofFileId) : null;
  const alt = `Payment screenshot from ${booking.studentName || 'the student'}`;
  const heldByPayment = Boolean(booking.coveredByPayment);

  const openDecline = () => {
    setError(null);
    setDeclining(true);
  };

  const keepBooking = () => {
    setError(null);
    setDeclining(false);
  };

  const run = async (kind, reason) => {
    if (sending.current) return;
    const action = ACTIONS[kind];
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      const args = kind === 'decline' ? declineRequestArgs(booking.id, reason) : [`/bookings/${booking.id}/${action.path}`];
      await api.patch(...args);
      setDeclining(false);
      onChanged?.({ status: action.status, message: action.message });
    } catch (err) {
      if (err?.status === CONFLICT_STATUS) {
        setDeclining(false);
        onChanged?.({ status: 'conflict', message: ALREADY_UPDATED_MESSAGE });
      } else {
        setError(kind === 'decline' ? declineErrorMessage(err) : action.failed);
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <div data-testid="payment-proof-panel" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      {proofUrl && (
        <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <ImageLightbox
            src={proofUrl}
            alt={alt}
            thumbStyle={{ width: 64, height: 88, objectFit: 'cover', objectPosition: 'top', borderRadius: 8, border: '1px solid ' + COLORS.border, display: 'block' }}
          />
          <figcaption style={{ fontFamily: FONT, fontSize: type.caption, color: COLORS.driftwood }}>Tap to enlarge</figcaption>
        </figure>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
        <PaymentSentBadge booking={booking} />
        {declining ? (
          <DeclineConfirm busy={busy} error={error} onConfirm={(reason) => run('decline', reason)} onCancel={keepBooking} />
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!heldByPayment && <Btn size="sm" onClick={() => run('confirm')} disabled={busy}>Confirm — payment received</Btn>}
            <Btn size="sm" variant="ghost" onClick={openDecline} disabled={busy}>Decline</Btn>
          </div>
        )}
        {heldByPayment && !declining && (
          <span style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.walnut, lineHeight: 1.5, maxWidth: 320 }}>{CONFIRMS_ON_PAYMENT_NOTE}</span>
        )}
        {error && !declining && (
          <span role="alert" style={{ fontFamily: FONT, fontSize: type.label, color: COLORS.errorRed, lineHeight: 1.5, maxWidth: 320 }}>{error}</span>
        )}
      </div>
    </div>
  );
}
